/* eslint-disable max-lines-per-function */
import { ReactElement, useCallback, useMemo } from 'react';
import { noop, sum } from 'lodash';

import { PrescriptionHistoryDailyGroup, PrescriptionHistoryQuery } from '@dreryk/egabinet-prescription-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';

import { Slot } from '@dreryk/watermelon';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import {
   AlignItems,
   DynamicColumnWidth,
   JustifyContent,
   Padding,
   RowDetailsExpander,
   RowDetailsRenderer,
   StyledFlex,
   TableColumnConfiguration,
   Text,
   UncontrolledCheckbox,
   ValueRendererProps,
} from '@dreryk/react-components';
import { dateFormatter } from '@dreryk/react-common';

import { useGetCurrentPractitionerGlobalState } from '~libs.shared.account';
import { protectFeature } from '~libs.shared.system';

import { translationKeys } from '../translations';

import {
   PrescriptionHistoryFilters,
   PrescriptionHistoryRenderAccordionRowActions,
   PrescriptionHistoryRenderOptionsCell,
   PrescriptionHistorySelection,
} from './PatientPrescriptionHistoryTable.types';
import { PrescriptionHistoryRowDetails } from './PrescriptionHistoryRowDetails/PrescriptionHistoryRowDetails';
import { getSelectablePrescriptionIds, resolvePractitionerIds } from './PatientPrescriptionHistoryTable.utils';

const FIXED_COLUMNS_WIDTHS_IN_PX = {
   checkbox: 28,
   safetyIndicators: 82,
   package: 157,
   quantity: 72,
   issuedFrom: 90,
   expander: 97,
};

const MEDICINE_NAME_SHARE = 0.6;
const DOSAGE_SHARE = 0.4;

type PrescriptionHistoryColumnsWidths = Record<
   keyof typeof FIXED_COLUMNS_WIDTHS_IN_PX | 'medicineName' | 'dosage',
   DynamicColumnWidth
>;

export const generateColumnsWidths = (
   medicineDatabaseExtendedAvailable: boolean,
   selectable?: boolean
): PrescriptionHistoryColumnsWidths => {
   const fixedWidths = {
      ...FIXED_COLUMNS_WIDTHS_IN_PX,
      checkbox: selectable ? FIXED_COLUMNS_WIDTHS_IN_PX.checkbox : 0,
      safetyIndicators: medicineDatabaseExtendedAvailable ? FIXED_COLUMNS_WIDTHS_IN_PX.safetyIndicators : 0,
   };
   const shareOfRemainingWidth = (share: number): DynamicColumnWidth =>
      `calc((100% - ${sum(Object.values(fixedWidths))}px) * ${share})`;

   return {
      checkbox: `${fixedWidths.checkbox}px`,
      medicineName: shareOfRemainingWidth(MEDICINE_NAME_SHARE),
      safetyIndicators: `${fixedWidths.safetyIndicators}px`,
      dosage: shareOfRemainingWidth(DOSAGE_SHARE),
      package: `${fixedWidths.package}px`,
      quantity: `${fixedWidths.quantity}px`,
      issuedFrom: `${fixedWidths.issuedFrom}px`,
      expander: `${fixedWidths.expander}px`,
   };
};

export const usePatientPrescriptionHistoryTableColumns = (
   renderAccordionRowActions?: PrescriptionHistoryRenderAccordionRowActions,
   selection?: PrescriptionHistorySelection
): TableColumnConfiguration<PrescriptionHistoryDailyGroup>[] => {
   const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');

   const columnsWidth = generateColumnsWidths(medicineDatabaseExtendedAvailable, Boolean(selection));

   return useMemo((): TableColumnConfiguration<PrescriptionHistoryDailyGroup>[] => {
      return [
         selection
            ? {
                 name: 'checkbox',
                 accessor: noop,
                 label: '',
                 width: columnsWidth.checkbox,
                 sortingDisabled: true,
                 renderer: (props: ValueRendererProps<PrescriptionHistoryDailyGroup>) => {
                    const allSelected = selection.isAllSelectedForDay(props.bean);
                    if (!getSelectablePrescriptionIds(props.bean).length) {
                       return null;
                    }
                    return (
                       <UncontrolledCheckbox
                          testId={`selectAllForDay-${String(props.bean.issuedOn)}`}
                          selected={allSelected}
                          onSelectionChange={() => selection.toggleAllForDay(props.bean)}
                          id={`selectAllForDay-${String(props.bean.issuedOn)}`}
                          name={`selectAllForDay-${String(props.bean.issuedOn)}`}
                       />
                    );
                 },
              }
            : null,
         {
            name: 'medicineName',
            accessor: noop,
            label: translationKeys.table.medicineName,
            width: columnsWidth.medicineName,
            sortingDisabled: true,
            renderer: (props: ValueRendererProps<PrescriptionHistoryDailyGroup>) => {
               return (
                  <StyledFlex gap={'8px'} alignItems={'center'}>
                     <Text as={'strong'} testId={'issuedOn'}>
                        {dateFormatter(props.bean.issuedOn)}
                     </Text>
                     {renderAccordionRowActions?.(props.bean)}
                  </StyledFlex>
               );
            },
         },
         medicineDatabaseExtendedAvailable
            ? {
                 name: 'safetyIndicators',
                 accessor: noop,
                 width: columnsWidth.safetyIndicators,
                 sortingDisabled: true,
                 renderer: () => null,
              }
            : null,
         {
            name: 'dosage',
            accessor: noop,
            label: translationKeys.table.dosage,
            width: columnsWidth.dosage,
            sortingDisabled: true,
            renderer: () => null,
         },
         {
            name: 'package',
            accessor: noop,
            label: translationKeys.table.package,
            width: columnsWidth.package,
            sortingDisabled: true,
            renderer: () => null,
         },
         {
            name: 'quantity',
            accessor: noop,
            label: translationKeys.table.quantity,
            width: columnsWidth.quantity,
            sortingDisabled: true,
            renderer: () => null,
         },
         {
            name: 'issuedFrom',
            accessor: noop,
            label: translationKeys.table.issuedFrom,
            width: columnsWidth.issuedFrom,
            alignItems: AlignItems.CENTER,
            padding: Padding.NONE,
            sortingDisabled: true,
            renderer: () => null,
         },
         {
            name: 'expander',
            accessor: noop,
            label: '',
            width: columnsWidth.expander,
            sortingDisabled: true,
            justifyContent: JustifyContent.END,
            renderer: (props: ValueRendererProps<PrescriptionHistoryDailyGroup>): ReactElement => {
               return <RowDetailsExpander {...props} testId={'expander'} />;
            },
         },
      ].filter(Boolean) as TableColumnConfiguration<PrescriptionHistoryDailyGroup>[];
   }, [medicineDatabaseExtendedAvailable, renderAccordionRowActions, columnsWidth, selection]);
};

export const usePatientPrescriptionHistoryRowDetailsRenderer = (
   renderOptionsCell?: PrescriptionHistoryRenderOptionsCell,
   selection?: PrescriptionHistorySelection
): RowDetailsRenderer<PrescriptionHistoryDailyGroup> =>
   useCallback<RowDetailsRenderer<PrescriptionHistoryDailyGroup>>(
      ({ bean }) => {
         return bean.prescriptions.map((prescription) => (
            <PrescriptionHistoryRowDetails
               key={prescription.prescriptionId}
               prescription={prescription}
               renderOptionsCell={renderOptionsCell}
               selection={selection}
            />
         ));
      },
      [renderOptionsCell, selection]
   );

export const usePatientPrescriptionHistoryTableDataConfiguration = (
   patientId: number,
   filters: PrescriptionHistoryFilters
): LazyLoadedDataConfigurationByCqrsQuery<PrescriptionHistoryDailyGroup> => {
   const currentPractitioner = useGetCurrentPractitionerGlobalState();
   return useLazyLoadedDataConfigurationByCqrsQuery<PrescriptionHistoryDailyGroup>(() => {
      const query = new PrescriptionHistoryQuery();
      query.patientId = PatientId.of(patientId);
      query.practitionerIds = resolvePractitionerIds(filters, currentPractitioner.id);
      query.continuouslyUsedMedicine = filters.continuouslyUsedMedicine ? Slot.of(true) : Slot.empty();
      return query;
   });
};
